import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { Icon } from './Icon'
import { useViewport } from '../../lib/use-viewport'
import { useReducedMotion } from '../../lib/use-reduced-motion'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  /** Optional secondary line rendered under the title in the header (e.g. a
   *  job role beneath a person's name). */
  subtitle?: string
  /** Optional element rendered to the LEFT of the title/subtitle block in the
   *  header (e.g. a person's avatar beside their name). */
  titleLeading?: ReactNode
  /** Title font size in px. Defaults to 20; drop it (e.g. 18) when the header
   *  pairs the title with a leading avatar so the two read as balanced. */
  titleSize?: number
  children: ReactNode
  hideCloseButton?: boolean
  footer?: ReactNode
  // Desktop only — mobile is always full-bleed bottom sheet. Override when
  // the dialog hosts a media preview (doc viewer, image lightbox) that
  // needs more room than the default form-style 560px panel. Accepts any
  // CSS length (e.g. ``900`` → ``900px``, or ``"min(960px, 90vw)"``).
  maxWidth?: number | string
  // Desktop only — override the default 90vh cap. Accepts any CSS length.
  // Pass ``'80vh'`` to leave more breathing room on tall content modals.
  maxHeight?: number | string
}

const PANEL_BASE: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'var(--shadow-modal)',
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  titleLeading,
  titleSize = 20,
  children,
  hideCloseButton = false,
  footer,
  maxWidth = 560,
  maxHeight = '90vh',
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const inertedElements = useRef<Element[]>([])
  const [entered, setEntered] = useState<boolean>(false)
  const { isMobile } = useViewport()
  const reducedMotion = useReducedMotion()

  // Stable ref for onClose so the focus-trap effect doesn't re-run on every
  // parent re-render (which would steal focus back to the first focusable
  // element on each keystroke inside the dialog).
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Drag-to-dismiss state (mobile only)
  const dragStartY = useRef<number>(0)
  const [dragOffset, setDragOffset] = useState<number>(0)
  const isDragging = useRef<boolean>(false)

  useEffect(() => {
    if (!open) { setEntered(false); return }
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [open])

  useEffect(() => {
    if (!open) return
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current(); return }
      if (event.key === 'Tab') {
        const root = dialogRef.current
        if (!root) return
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (!first || !last) return
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    dialogRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )?.[0]?.focus()

    const portalContainer = dialogRef.current?.closest('body > *') ?? null
    const toInert = Array.from(document.body.children).filter((el) => {
      if (el === portalContainer) return false
      const tag = el.tagName.toLowerCase()
      return tag !== 'script' && tag !== 'style'
    })
    toInert.forEach((el) => el.setAttribute('inert', ''))
    inertedElements.current = toInert

    return () => {
      document.removeEventListener('keydown', handleKey)
      previouslyFocused.current?.focus()
      inertedElements.current.forEach((el) => el.removeAttribute('inert'))
      inertedElements.current = []
    }
  }, [open])

  if (!open) return null

  function handlePointerDown(event: React.PointerEvent<HTMLButtonElement>): void {
    event.currentTarget.setPointerCapture(event.pointerId)
    isDragging.current = true
    dragStartY.current = event.clientY
  }

  function handlePointerMove(event: React.PointerEvent<HTMLButtonElement>): void {
    if (!isDragging.current) return
    setDragOffset(Math.max(0, event.clientY - dragStartY.current))
  }

  function handlePointerUp(event: React.PointerEvent<HTMLButtonElement>): void {
    if (!isDragging.current) return
    isDragging.current = false
    event.currentTarget.releasePointerCapture(event.pointerId)
    const panelHeight = dialogRef.current?.offsetHeight ?? 0
    const dismissed = panelHeight > 0 && dragOffset > panelHeight * 0.3
    setDragOffset(0)
    if (dismissed) onClose()
  }

  const motionTransition = reducedMotion
    ? 'none'
    : 'opacity var(--motion-duration-base) var(--motion-ease-out),' +
      ' transform var(--motion-duration-base) var(--motion-ease-out)'

  const panelStyle: React.CSSProperties = isMobile
    ? {
        ...PANEL_BASE,
        borderRadius: '16px 16px 0 0',
        width: '100%',
        transform: dragOffset > 0
          ? `translateY(${dragOffset}px)`
          : `translateY(${reducedMotion || entered ? '0px' : '100%'})`,
        transition: dragOffset > 0 ? 'none' : motionTransition,
        willChange: 'transform',
      }
    : {
        ...PANEL_BASE,
        borderRadius: 16,
        maxWidth,
        maxHeight,
        width: '100%',
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0)' : 'translateY(8px)',
        transition: motionTransition,
      }

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(42,38,34,0.45)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: isMobile ? 'stretch' : 'center',
        padding: isMobile ? 0 : 24,
        zIndex: 100,
        opacity: entered ? 1 : 0,
        transition: motionTransition,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'dialog'}
        onClick={(event) => event.stopPropagation()}
        style={panelStyle}
      >
        {/* Mobile drag handle — doubles as close button */}
        {isMobile && (
          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: 8,
              paddingBottom: 4,
              width: '100%',
              background: 'transparent',
              border: 'none',
              cursor: 'grab',
              flexShrink: 0,
              touchAction: 'none',
            }}
          >
            <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--text-3)' }} />
          </button>
        )}

        {/* Header: title + close button */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 24px 0', flexShrink: 0 }}>
          {title
            ? (
              <div style={{ flex: 1, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                {titleLeading != null ? <div style={{ flexShrink: 0 }}>{titleLeading}</div> : null}
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ fontSize: titleSize, margin: 0 }}>{title}</h2>
                  {subtitle
                    ? <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.4 }}>{subtitle}</div>
                    : null}
                </div>
              </div>
            )
            : <span style={{ flex: 1 }} />
          }
          {!hideCloseButton && !isMobile && (
            <button
              aria-label="Close dialog"
              onClick={onClose}
              style={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer',
                padding: 6, marginLeft: 8, flexShrink: 0, opacity: 1,
                transition: 'background 80ms ease, opacity 80ms ease',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-2)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
              onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.7' }}
              onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
            >
              <Icon name="close" size={20} />
            </button>
          )}
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
          {children}
        </div>

        {/* Optional footer */}
        {footer != null && (
          <div
            style={{
              flexShrink: 0,
              paddingTop: 12,
              paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom))' : 12,
              paddingLeft: 16,
              paddingRight: 16,
              borderTop: '1px solid var(--border)',
              background: 'var(--surface)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
